import axios from 'axios'
import jwtDecode from 'jwt-decode'
import moment from 'moment'
import MUIDataTable from 'mui-datatables'
import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useHistory, useLocation } from 'react-router-dom'
import { Button, Card, CardBody } from 'reactstrap'
import { useDebounce } from '../../../utils/searchDebouncer'
import ContentWrapper from '../../Layout/ContentWrapper'
import ManagerFeedbackSurveyModal from './ManagerFeedbackSurveyModal'
import ForwardSurveyModal from './ForwardSurveyModal'
import { office_location } from '../../../modules/shared/enums/office_location'

const ADMIN_LEVELS = [1, 5, 6]

const getUserLevel = () => {
  try {
    const token = localStorage.getItem('token')
    return token ? jwtDecode(token).user_level : null
  } catch {
    return null
  }
}

const getUserId = () => {
  try {
    const token = localStorage.getItem('token')
    return token ? jwtDecode(token).user_id : null
  } catch {
    return null
  }
}

const SUPER_ADMIN_LEVEL = 1

const STATUS_LABEL = {
  pending: { label: 'Në pritje', color: '#fd7e14' },
  completed: { label: 'E plotësuar', color: '#28a745' },
}

const TYPE_LABEL = {
  probation: 'Periudha Provuese',
  exit: 'Largim',
  renewal: 'Vazhdim i Kontratës',
}

const fetchSurveys = async ({ name, type, status, office_location: officeLocation }) => {
  const isAdmin = ADMIN_LEVELS.includes(getUserLevel())
  const params = {}
  if (name) params.name = name
  if (type) params.type = type
  if (status) params.status = status
  if (officeLocation) params.office_location = officeLocation
  const url = isAdmin ? 'manager-feedback-surveys' : 'manager-feedback-surveys/mine'
  const res = await axios.get(url, { params })
  return res.data.data || []
}

// Temporarily disable automatically opening overdue surveys on this page.
const AUTO_OPEN_OVERDUE_SURVEYS = false

const fetchNextRequiredOverdueSurvey = async () => {
  const res = await axios.get('manager-feedback-surveys/overdue-required/next')
  return res.data.data || null
}

const ManagerFeedbackSurveys = () => {
  const history = useHistory()
  const location = useLocation()
  const queryClient = useQueryClient()

  const currentUserId = getUserId()
  const isAdmin = ADMIN_LEVELS.includes(getUserLevel())
  const isSuperAdmin = getUserLevel() === SUPER_ADMIN_LEVEL
  // who is allowed to FILL a survey: the assigned manager, or super admin (override)
  const canFillSurvey = (row) =>
    row?.status === 'pending' && (isSuperAdmin || row?.manager?._id === currentUserId)

  // type/status/office_location are applied with the buttons; name search is debounced
  const [filters, setFilters] = useState({ type: '', status: '', office_location: '' })
  // draft filter inputs
  const [draftName, setDraftName] = useState('')
  const [draftType, setDraftType] = useState('')
  const [draftStatus, setDraftStatus] = useState('')
  const [draftOfficeLocation, setDraftOfficeLocation] = useState('')
  const debouncedName = useDebounce(draftName.trim(), 300)
  const searchName = debouncedName.length >= 3 ? debouncedName : ''

  const [modal, setModal] = useState({
    open: false,
    surveyId: null,
    surveyData: null,
    readOnly: true,
    mandatory: false,
  })
  const [forwardModal, setForwardModal] = useState({ open: false, surveyId: null, currentManagerId: null })
  const handledRequiredSurveyIds = useRef(new Set())
  const [pausedRequiredSurveyId, setPausedRequiredSurveyId] = useState(null)

  const { data: surveys = [], isFetching } = useQuery({
    // currentUserId is included so a different login doesn't reuse another
    // user's cached list (the QueryClient persists across login/logout).
    queryKey: [
      'manager-surveys',
      currentUserId,
      searchName,
      filters.type,
      filters.status,
      filters.office_location,
    ],
    queryFn: () => fetchSurveys({ ...filters, name: searchName }),
    staleTime: 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  })

  const { data: nextRequiredOverdueSurvey } = useQuery({
    queryKey: ['manager-surveys', 'overdue-required-next', currentUserId],
    queryFn: fetchNextRequiredOverdueSurvey,
    enabled: AUTO_OPEN_OVERDUE_SURVEYS && Boolean(currentUserId) && !isSuperAdmin,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  })

  const data = surveys

  // open the modal from a ?surveyId= deep link (notification click)
  useEffect(() => {
    const surveyId = new URLSearchParams(location.search).get('surveyId')
    if (!surveyId) return

    const fromList = data.find((d) => d._id === surveyId)
    if (fromList) {
      setModal({
        open: true,
        surveyId,
        surveyData: fromList,
        readOnly: fromList.status !== 'pending',
        mandatory: false,
      })
      history.replace(location.pathname)
      return
    }
    // not in the cached list yet (cold load) — fetch just this one.
    // Note: don't clear the query param until after we've opened the modal,
    // otherwise changing location.search would re-trigger this effect's cleanup.
    let cancelled = false
    axios
      .get(`manager-feedback-surveys/${surveyId}`)
      .then((res) => {
        if (cancelled) return
        const survey = res.data.data
        setModal({
          open: true,
          surveyId,
          surveyData: survey,
          readOnly: survey.status !== 'pending',
          mandatory: false,
        })
        history.replace(location.pathname)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [location.search])

  useEffect(() => {
    const hasSurveyDeepLink = new URLSearchParams(location.search).has('surveyId')
    if (
      !AUTO_OPEN_OVERDUE_SURVEYS ||
      !nextRequiredOverdueSurvey ||
      hasSurveyDeepLink ||
      modal.open ||
      pausedRequiredSurveyId ||
      handledRequiredSurveyIds.current.has(nextRequiredOverdueSurvey._id)
    ) return

    setModal({
      open: true,
      surveyId: nextRequiredOverdueSurvey._id,
      surveyData: nextRequiredOverdueSurvey,
      readOnly: false,
      mandatory: true,
    })
  }, [nextRequiredOverdueSurvey, location.search, modal.open, pausedRequiredSurveyId])

  const applyFilters = () =>
    setFilters({ type: draftType, status: draftStatus, office_location: draftOfficeLocation })

  const clearFilters = () => {
    setDraftName('')
    setDraftType('')
    setDraftStatus('')
    setDraftOfficeLocation('')
    setFilters({ type: '', status: '', office_location: '' })
  }

  const openModal = (id, isPending = false) => {
    const fromList = data.find((d) => d._id === id)
    setModal({
      open: true,
      surveyId: id,
      surveyData: fromList || null,
      readOnly: !isPending,
      mandatory: false,
    })
  }

  const closeModal = () => {
    if (
      modal.mandatory &&
      modal.surveyId &&
      !handledRequiredSurveyIds.current.has(modal.surveyId)
    ) setPausedRequiredSurveyId(modal.surveyId)
    setModal({ open: false, surveyId: null, surveyData: null, readOnly: true, mandatory: false })
  }

  const onSubmitted = (updatedSurvey) => {
    if (updatedSurvey?._id) {
      handledRequiredSurveyIds.current.add(updatedSurvey._id)
      if (pausedRequiredSurveyId === updatedSurvey._id) setPausedRequiredSurveyId(null)
    }
    // A submitted survey changes status (pending -> completed), so an in-place
    // cache update would wrongly keep it in a status-filtered list. Invalidate
    // every cached list (all filter combinations) so they refetch fresh.
    queryClient.invalidateQueries({ queryKey: ['manager-surveys'] })
  }

  const openForwardModal = (row) =>
    setForwardModal({ open: true, surveyId: row._id, currentManagerId: row.manager?._id })

  const closeForwardModal = () =>
    setForwardModal({ open: false, surveyId: null, currentManagerId: null })

  const onForwarded = (surveyId) => {
    if (!surveyId) return
    handledRequiredSurveyIds.current.add(surveyId)
    if (pausedRequiredSurveyId === surveyId) setPausedRequiredSurveyId(null)
  }

  const columns = [
    {
      name: 'employee',
      label: 'Punonjësi',
      options: {
        customBodyRender: (val) => (val ? `${val.first_name} ${val.last_name}` : '—'),
      },
    },
    {
      name: 'employee',
      label: 'Pozita',
      options: {
        customBodyRender: (val) => val?.function?.name || '—',
      },
    },
    {
      name: 'manager',
      label: 'Menaxheri',
      options: {
        customBodyRender: (val, tableMeta) => {
          if (!val) return 'I pacaktuar'
          const idColIndex = columns.findIndex((column) => column.name === '_id')
          const surveyId = tableMeta.rowData?.[idColIndex]
          const row = data.find((survey) => survey._id === surveyId)
          const lastForward = row?.forwardHistory?.[row.forwardHistory.length - 1]
          const forwardedBy = lastForward?.by
          return (
            <div>
              <div>{`${val.first_name} ${val.last_name}`}</div>
              {forwardedBy && (
                <div style={{ fontSize: '0.72rem', color: '#6c757d' }}>
                  {`Përcjellë nga: ${forwardedBy.first_name} ${forwardedBy.last_name}`}
                </div>
              )}
            </div>
          )
        },
      },
    },
    {
      name: 'type',
      label: 'Lloji',
      options: {
        customBodyRender: (val) => TYPE_LABEL[val] || val,
      },
    },
    {
      name: 'status',
      label: 'Statusi',
      options: {
        customBodyRender: (val, tableMeta) => {
          const s = STATUS_LABEL[val] || { label: val, color: '#6c757d' }
          const idColIndex = columns.findIndex((c) => c.name === '_id')
          const surveyId = tableMeta.rowData?.[idColIndex]
          const row = data.find((d) => d._id === surveyId)
          const isOverdue = val === 'pending' && row?.reminderSentAt
          return (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span
                style={{
                  backgroundColor: s.color,
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: 12,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  display: 'inline-block',
                }}
              >
                {s.label}
              </span>
              {isOverdue && (
                <span
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: 12,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    display: 'inline-block',
                  }}
                >
                  Vonuar
                </span>
              )}
            </div>
          )
        },
      },
    },
    {
      name: 'sentAt',
      label: 'Dërguar më',
      options: {
        customBodyRender: (val) => (val ? moment(val).format('DD/MM/YYYY HH:mm') : '—'),
      },
    },
    {
      name: 'completedAt',
      label: 'Plotësuar më',
      options: {
        customBodyRender: (val) => (val ? moment(val).format('DD/MM/YYYY HH:mm') : '—'),
      },
    },
    {
      name: '_id',
      label: 'Veprime',
      options: {
        customBodyRender: (id) => {
          const row = data.find((d) => d._id === id)
          const canFill = canFillSurvey(row)
          const canForward =
            row?.status === 'pending' &&
            (isSuperAdmin || row?.manager?._id === currentUserId || (isAdmin && !row?.manager))
          return (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <Button size='sm' color={canFill ? 'success' : 'primary'} onClick={() => openModal(id, canFill)}>
                {canFill ? 'Plotëso' : 'Shiko'}
              </Button>
              {canForward && (
                <Button size='sm' color='info' outline onClick={() => openForwardModal(row)}>
                  {row?.manager ? 'Përcille' : 'Cakto menaxherin'}
                </Button>
              )}
            </div>
          )
        },
      },
    },
  ]

  const options = {
    selectableRows: 'none',
    rowsPerPage: 15,
    rowsPerPageOptions: [15, 30, 50],
    print: false,
    download: false,
    search: true,
    responsive: 'scroll',
    textLabels: {
      body: { noMatch: isFetching ? 'Duke ngarkuar...' : 'Nuk u gjetën të dhëna' },
    },
  }

  return (
    <ContentWrapper>
      <div className='content-heading'>
        <div>Performance Feedback</div>
      </div>

      <Card className='mb-3'>
        <CardBody>
          <style>{`
            @media (max-width: 576px) {
              .survey-filter-bar { flex-direction: column !important; }
              .survey-filter-bar input,
              .survey-filter-bar select { width: 100% !important; }
              .survey-filter-actions { width: 100%; display: flex; gap: 8px; }
              .survey-filter-actions button { flex: 1; }
            }
          `}</style>
          <div className='survey-filter-bar' style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type='text'
              className='form-control'
              style={{ width: 220 }}
              placeholder='Kerko perdoruesin'
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
            />

            <select
              className='form-control'
              style={{ width: 180 }}
              value={draftType}
              onChange={(e) => setDraftType(e.target.value)}
            >
              <option value=''>Të gjitha llojet</option>
              <option value='probation'>Periudha Provuese</option>
              <option value='exit'>Largim</option>
              <option value='renewal'>Vazhdim i Kontratës</option>
            </select>

            <select
              className='form-control'
              style={{ width: 180 }}
              value={draftStatus}
              onChange={(e) => setDraftStatus(e.target.value)}
            >
              <option value=''>Të gjitha statuset</option>
              <option value='pending'>Në pritje</option>
              <option value='completed'>E plotësuar</option>
            </select>

            <select
              className='form-control'
              style={{ width: 180 }}
              value={draftOfficeLocation}
              onChange={(e) => setDraftOfficeLocation(e.target.value)}
            >
              <option value=''>Të gjitha bashkitë</option>
              {office_location.map((loc) => (
                <option key={loc.value} value={loc.value}>
                  {loc.name}
                </option>
              ))}
            </select>

            <div className='survey-filter-actions' style={{ display: 'flex', gap: 8 }}>
              <Button color='primary' onClick={applyFilters}>
                Filtro
              </Button>
              <Button color='secondary' onClick={clearFilters}>
                Pastro
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>

      <MUIDataTable
        key={`${searchName}|${filters.type}|${filters.status}|${filters.office_location}`}
        title='Lista e Sondazheve'
        data={data}
        columns={columns}
        options={options}
      />

      {modal.open && (
        <ManagerFeedbackSurveyModal
          surveyId={modal.surveyId}
          surveyData={modal.surveyData}
          open={modal.open}
          onClose={closeModal}
          onSubmitted={onSubmitted}
          readOnly={modal.readOnly}
        />
      )}

      <ForwardSurveyModal
        surveyId={forwardModal.surveyId}
        currentManagerId={forwardModal.currentManagerId}
        isOpen={forwardModal.open}
        onClose={closeForwardModal}
        onForwarded={onForwarded}
      />

    </ContentWrapper>
  )
}

export default ManagerFeedbackSurveys
